import { relations, sql } from 'drizzle-orm';
import { boolean, date, decimal, index, integer, jsonb, pgEnum, pgTable, text, timestamp, uuid, } from 'drizzle-orm/pg-core';
import * as z from 'zod';
import { user } from './auth.js';
import { applicationForms, treatmentChairs } from './basic-data.js';
import { drugCatalogItems } from './drug-catalog.js';
import { DoseCalcModeActive, FormType, doseCalcModeActiveEnum, formTypeEnum, gfrMethodEnum, preparationTypeEnum, prescriptionSourceEnum, productionSourceEnum, qaResultEnum, serumUnitEnum, volumeModeEnum, } from './enums.js';
import { cleanroomStates } from './cleanroom-state.js';
import { patients } from './patients.js';
import { AdministrationModeEnum, bsaFormulaEnum } from './settings.js';
import { productionDetails } from './production-details.js';
import { orderBatches, orders } from './production-orders.js';
import { consumables, prescribedNames } from './products.js';
import { protocols } from './protocols.js';
import { timestamps } from './utils/index.js';
import { userActions } from './utils/user-actions.js';
import { uuidv7 } from './utils/uuidv7.js';
export const PatientTherapySequenceStatus = z.enum([
    'pending_approval', // 0: Optional Board sign-off
    'pending_doctor_validation', // 1: Initial Medical Review
    'validating', // 2: Parallel phase (Pharma check + Doctor release)
    'production_planning', // 3: Preparation in the Cleanroom queue
    'in_production', // 4: Physical compounding
    'qa_dispatch', // 5: Final Pharmacist check before delivery
    'released_for_delivery', // 6: In transit to the ward
    'administration', // 7: Bedside verification
    'administered', // Final: Patient received dose
    'returned', // Edge: Stability check pending
    'discarded', // Final: Wastage/Spoilage
    'budget_not_approved',
    'cancelled', // Final: Protocol stopped or removed while the prescription was still open
]);
export const patientTherapySequenceStatusEnum = pgEnum('patient_therapy_sequence_status', [
    PatientTherapySequenceStatus.enum.pending_approval,
    PatientTherapySequenceStatus.enum.pending_doctor_validation,
    PatientTherapySequenceStatus.enum.validating,
    PatientTherapySequenceStatus.enum.production_planning,
    PatientTherapySequenceStatus.enum.in_production,
    PatientTherapySequenceStatus.enum.qa_dispatch,
    PatientTherapySequenceStatus.enum.released_for_delivery,
    PatientTherapySequenceStatus.enum.administration,
    PatientTherapySequenceStatus.enum.administered,
    PatientTherapySequenceStatus.enum.returned,
    PatientTherapySequenceStatus.enum.discarded,
    PatientTherapySequenceStatus.enum.budget_not_approved,
    PatientTherapySequenceStatus.enum.cancelled,
]);
export const patientProtocols = pgTable('patient_protocols', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    protocolId: uuid('protocol_id')
        .notNull()
        .references(() => protocols.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    startDate: date('start_date').notNull(),
    endDate: date('end_date'),
    // Per-assignment override of the protocol template's totalCycles. Null on
    // pre-existing rows - fall back to protocol.totalCycles when reading.
    cycles: integer('cycles'),
    cycleStartDates: jsonb('cycle_start_dates').$type(),
    ...timestamps(),
    ...userActions(),
});
export const patientTherapySequence = pgTable('patient_therapy_sequence', {
    id: uuidv7('id').primaryKey(),
    cyproOldId: integer('cypro_old_id'),
    internalId: text('internal_id'),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    protocolId: uuid('protocol_id').references(() => protocols.id, {
        onDelete: 'set null',
        onUpdate: 'cascade',
    }),
    patientProtocolId: uuid('patient_protocol_id').references(() => patientProtocols.id, {
        onDelete: 'set null',
        onUpdate: 'cascade',
    }),
    sequenceGroupId: uuidv7('sequence_group_id'),
    therapyDate: date('therapy_date').notNull(),
    status: patientTherapySequenceStatusEnum('status')
        .notNull()
        .default(PatientTherapySequenceStatus.enum.pending_doctor_validation),
    pharmacistValidatedBy: uuid('pharmacist_validated_by').references(() => user.id),
    pharmacistValidatedAt: timestamp('pharmacist_validated_at'),
    patientArrivedAt: timestamp('patient_arrived_at'),
    doctorValidatedBy: uuid('doctor_validated_by').references(() => user.id),
    doctorValidatedAt: timestamp('doctor_validated_at'),
    // Responsible doctor for this prescription; prefilled from the patient's responsible doctor.
    responsibleDoctorId: uuid('responsible_doctor_id').references(() => user.id, {
        onDelete: 'set null',
        onUpdate: 'cascade',
    }),
    administeredBy: uuid('administered_by').references(() => user.id),
    administeredAt: timestamp('administered_at'),
    // Administration mode used and, in Scan mode, whether the patient scan was skipped; null when the
    // prescription was administered on dispatch or before CY-212.
    administrationMode: AdministrationModeEnum('administration_mode'),
    patientScanSkipped: boolean('patient_scan_skipped'),
    // true when the release marked it Administered (Mark preparations as administered on dispatch), so it can
    // still be returned; false for a nurse administration, null on older rows (CY-332)
    administeredOnDispatch: boolean('administered_on_dispatch'),
    // when production completed and when the bag was released; the Reports date bases and fields read them (CY-356)
    producedAt: timestamp('produced_at'),
    releasedAt: timestamp('released_at'),
    // Reports filters (CY-353): who completed production, what it drew from, whether it left a rest vial,
    // and the QA outcome. prescriptionSource stays cypro until the hospital interface creates prescriptions.
    producedBy: uuid('produced_by').references(() => user.id, {
        onDelete: 'set null',
        onUpdate: 'cascade',
    }),
    productionSource: productionSourceEnum('production_source'),
    restVialCreated: boolean('rest_vial_created').notNull().default(false),
    qaResult: qaResultEnum('qa_result'),
    prescriptionSource: prescriptionSourceEnum('prescription_source').notNull().default('cypro'),
    returnedBy: uuid('returned_by').references(() => user.id),
    returnedAt: timestamp('returned_at'),
    cancelledBy: uuid('cancelled_by').references(() => user.id),
    cancelledAt: timestamp('cancelled_at'),
    cancellationReason: text('cancellation_reason'),
    discardedBy: uuid('discarded_by').references(() => user.id),
    discardedAt: timestamp('discarded_at'),
    discardReason: text('discard_reason'),
    // free text for the QA reject reason Other (CY-340)
    discardNote: text('discard_note'),
    remadeBy: uuid('remade_by').references(() => user.id),
    remadeAt: timestamp('remade_at'),
    // the prescription created by Start remake, so the original can link to it
    remadeToId: uuid('remade_to_id').references(() => patientTherapySequence.id, {
        onDelete: 'set null',
        onUpdate: 'cascade',
    }),
    isNote: boolean('is_note').notNull().default(false),
    note: text('note'),
    therapyNote: text('therapy_note'),
    pharmacyNote: text('pharmacy_note'),
    cycle: integer('cycle'),
    day: integer('day'),
    time: text('time').notNull(),
    carrierSolutionId: uuid('carrier_solution_id').references(() => drugCatalogItems.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    applicationFormId: uuid('application_form_id').references(() => applicationForms.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    orderId: uuid('order_id').references(() => orders.id, {
        onDelete: 'restrict',
        onUpdate: 'cascade',
    }),
    duration: integer('duration'),
    volume: integer('volume'),
    volumeMode: volumeModeEnum('volume_mode'),
    chairId: uuid('chair_id').references(() => treatmentChairs.id, {
        onDelete: 'set null',
        onUpdate: 'cascade',
    }),
    preparationType: preparationTypeEnum('preparation_type'),
    // Patient values the medicines' total amounts were calculated with; null when not recorded.
    doseBasisHeight: decimal('dose_basis_height', { mode: 'number' }),
    doseBasisWeight: decimal('dose_basis_weight', { mode: 'number' }),
    doseBasisBsa: decimal('dose_basis_bsa', { mode: 'number' }),
    doseBasisGfr: decimal('dose_basis_gfr', { mode: 'number' }),
    // AIBW and whether weight-based doses used it instead of doseBasisWeight (the patient's "Use AIBW")
    doseBasisAibw: decimal('dose_basis_aibw', { mode: 'number' }),
    doseBasisUseAibw: boolean('dose_basis_use_aibw'),
    // the characteristics' BSA formula, and the in-use GFR entry's method, sample date and serum creatinine
    doseBasisBsaFormula: bsaFormulaEnum('dose_basis_bsa_formula'),
    doseBasisGfrMethod: gfrMethodEnum('dose_basis_gfr_method'),
    doseBasisGfrLabTime: timestamp('dose_basis_gfr_lab_time'),
    doseBasisSerumCreatinine: decimal('dose_basis_serum_creatinine', { mode: 'number' }),
    doseBasisSerumUnit: serumUnitEnum('dose_basis_serum_unit'),
    // the patient's completed months of age when the dose was calculated
    doseBasisAgeMonths: integer('dose_basis_age_months'),
    // the remade prescription the total amounts and dose values were copied from; set while they still are the
    // ones the doctor validated there, so they aren't recalculated. Cleared once the doctor changes the dose (CY-303)
    doseCopiedFromSequenceId: uuid('dose_copied_from_sequence_id').references(() => patientTherapySequence.id, { onDelete: 'set null', onUpdate: 'cascade' }),
    // True when the order was dispatched to a smart compounder (automated chemo machine)
    // instead of the manual cleanroom compounding workflow.
    compoundedByMachine: boolean('compounded_by_machine').notNull().default(false),
    // Maintained by the sync_patient_therapy_sequence_medicine_names trigger: prescribed (trade) name, else active substance name, of each medicine joined with ' / '
    medicineNames: text('medicine_names'),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_patient_therapy_sequence_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    // patient Therapy tab: patient + day order
    index('idx_patient_therapy_sequence_patient_day')
        .on(t.patientId, t.therapyDate, t.time)
        .where(sql `${t.deletedAt} IS NULL`),
    // production list: day order with cursor pagination
    index('idx_patient_therapy_sequence_day_order')
        .on(t.therapyDate, t.time, t.id)
        .where(sql `${t.deletedAt} IS NULL`),
    // status queues (validation, production, QA) keep the day order
    index('idx_patient_therapy_sequence_status_day')
        .on(t.status, t.therapyDate, t.time)
        .where(sql `${t.deletedAt} IS NULL`),
    // label scan / exact prescription lookup and report search
    index('idx_patient_therapy_sequence_internal_id').on(t.internalId),
    index('idx_patient_therapy_sequence_order').on(t.orderId),
    index('idx_patient_therapy_sequence_protocol').on(t.protocolId),
    index('idx_patient_therapy_sequence_patient_protocol').on(t.patientProtocolId),
    index('idx_patient_therapy_sequence_created')
        .on(t.createdAt, t.id)
        .where(sql `${t.deletedAt} IS NULL`),
]);
export const patientTherapySequenceMedicine = pgTable('patient_therapy_sequence_medicine', {
    id: uuidv7('id').primaryKey(),
    therapySequenceId: uuid('therapy_sequence_id')
        .notNull()
        .references(() => patientTherapySequence.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    patientId: uuid('patient_id')
        .notNull()
        .references(() => patients.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    therapyDate: date('therapy_date').notNull(),
    activeSubstanceId: uuid('active_substance_id').references(() => drugCatalogItems.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
    }),
    prescribedNameId: uuid('prescribed_name_id').references(() => prescribedNames.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
    }),
    // snapshot of what the doctor released, so a later pharmacist trade-name change does not rewrite it
    doctorPrescribedNameId: uuid('doctor_prescribed_name_id').references(() => prescribedNames.id, {
        onDelete: 'set null',
        onUpdate: 'cascade',
    }),
    // the substance and total amount the doctor released, for a substance-only prescription
    doctorPrescribedActiveSubstanceId: uuid('doctor_prescribed_active_substance_id').references(() => drugCatalogItems.id, { onDelete: 'set null', onUpdate: 'cascade' }),
    doctorPrescribedTotalAmount: decimal('doctor_prescribed_total_amount', { mode: 'number' }),
    doctorPrescribedAt: timestamp('doctor_prescribed_at'),
    /**
     * If `false`, the pharmacist can prescribe a similar drug.
     *
     * If `true`, the pharmacist cannot prescribe another drug than the one prescribed by the doctor.
     */
    autIdem: boolean('aut_idem').notNull().default(false),
    formType: formTypeEnum('form_type').default(FormType.enum.OTHER),
    calculationMethod: doseCalcModeActiveEnum('calculation_method').default(DoseCalcModeActive.enum.ABSOLUTE),
    regularDose: decimal('regular_dose', { mode: 'number' }),
    recommendedDose: decimal('recommended_dose', { mode: 'number' }),
    precisionDecimals: integer('precision_decimals').default(0),
    doseProportion: decimal('dose_proportion', { mode: 'number' }),
    totalAmount: decimal('total_amount', { mode: 'number' }),
    preparationType: preparationTypeEnum('preparation_type'),
    notes: text('notes'),
    ageRanges: jsonb('age_ranges').$type(),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_patient_therapy_sequence_medicine_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
    // Used by the medicine_names sync trigger (migration 0249), which looks up
    // a sequence's medicines on every medicine insert/update/delete - without
    // it each trigger call scans the whole table.
    index('idx_patient_therapy_sequence_medicine_therapy_sequence_id').on(t.therapySequenceId),
    // drug history, report filters and prescribed-name lookups
    index('idx_patient_therapy_sequence_medicine_patient_date').on(t.patientId, t.therapyDate),
    index('idx_patient_therapy_sequence_medicine_active_substance').on(t.activeSubstanceId),
    index('idx_patient_therapy_sequence_medicine_prescribed_name').on(t.prescribedNameId),
]);
export const patientTherapySequenceObservations = pgTable('patient_therapy_sequence_observations', {
    id: uuidv7('id').primaryKey(),
    therapySequenceId: uuid('therapy_sequence_id')
        .notNull()
        .references(() => patientTherapySequence.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    note: text('note').notNull(),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_patient_therapy_sequence_observations_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
]);
export const patientTherapySequenceConsumables = pgTable('patient_therapy_sequence_consumables', {
    id: uuidv7('id').primaryKey(),
    therapySequenceId: uuid('therapy_sequence_id')
        .notNull()
        .references(() => patientTherapySequence.id, { onDelete: 'cascade', onUpdate: 'cascade' }),
    consumableId: uuid('consumable_id')
        .notNull()
        .references(() => consumables.id, { onDelete: 'restrict', onUpdate: 'cascade' }),
    quantity: integer('quantity').notNull(),
    ...timestamps(),
    ...userActions(),
}, t => [
    index('idx_patient_therapy_sequence_consumables_active')
        .on(t.deletedAt)
        .where(sql `${t.deletedAt} IS NULL`),
]);
export const patientProtocolsRelations = relations(patientProtocols, ({ one, many }) => ({
    patient: one(patients, {
        fields: [patientProtocols.patientId],
        references: [patients.id],
    }),
    protocol: one(protocols, {
        fields: [patientProtocols.protocolId],
        references: [protocols.id],
    }),
    therapySequence: many(patientTherapySequence),
    created: one(user, {
        fields: [patientProtocols.createdBy],
        references: [user.id],
    }),
}));
export const patientTherapySequenceRelations = relations(patientTherapySequence, ({ one, many }) => ({
    patient: one(patients, {
        fields: [patientTherapySequence.patientId],
        references: [patients.id],
    }),
    patientProtocol: one(patientProtocols, {
        fields: [patientTherapySequence.patientProtocolId],
        references: [patientProtocols.id],
    }),
    protocol: one(protocols, {
        fields: [patientTherapySequence.protocolId],
        references: [protocols.id],
    }),
    carrierSolution: one(drugCatalogItems, {
        fields: [patientTherapySequence.carrierSolutionId],
        references: [drugCatalogItems.id],
    }),
    applicationForm: one(applicationForms, {
        fields: [patientTherapySequence.applicationFormId],
        references: [applicationForms.id],
    }),
    order: one(orders, {
        fields: [patientTherapySequence.orderId],
        references: [orders.id],
    }),
    /** the batches this prescription actually used, one row per batch and role */
    batches: many(orderBatches),
    productionDetails: many(productionDetails),
    cleanroomState: one(cleanroomStates, {
        fields: [patientTherapySequence.id],
        references: [cleanroomStates.therapySequenceId],
    }),
    chair: one(treatmentChairs, {
        fields: [patientTherapySequence.chairId],
        references: [treatmentChairs.id],
    }),
    medicine: many(patientTherapySequenceMedicine),
    observations: many(patientTherapySequenceObservations),
    consumables: many(patientTherapySequenceConsumables),
    created: one(user, {
        fields: [patientTherapySequence.createdBy],
        references: [user.id],
    }),
    responsibleDoctor: one(user, {
        fields: [patientTherapySequence.responsibleDoctorId],
        references: [user.id],
    }),
    doctorValidated: one(user, {
        fields: [patientTherapySequence.doctorValidatedBy],
        references: [user.id],
    }),
    pharmacistValidated: one(user, {
        fields: [patientTherapySequence.pharmacistValidatedBy],
        references: [user.id],
    }),
    administeredByUser: one(user, {
        fields: [patientTherapySequence.administeredBy],
        references: [user.id],
    }),
}));
export const patientTherapySequenceMedicineRelations = relations(patientTherapySequenceMedicine, ({ one }) => ({
    therapySequence: one(patientTherapySequence, {
        fields: [patientTherapySequenceMedicine.therapySequenceId],
        references: [patientTherapySequence.id],
    }),
    patient: one(patients, {
        fields: [patientTherapySequenceMedicine.patientId],
        references: [patients.id],
    }),
    activeSubstance: one(drugCatalogItems, {
        fields: [patientTherapySequenceMedicine.activeSubstanceId],
        references: [drugCatalogItems.id],
    }),
    prescribedName: one(prescribedNames, {
        fields: [patientTherapySequenceMedicine.prescribedNameId],
        references: [prescribedNames.id],
    }),
    created: one(user, {
        fields: [patientTherapySequenceMedicine.createdBy],
        references: [user.id],
    }),
}));
export const patientTherapySequenceObservationRelations = relations(patientTherapySequenceObservations, ({ one }) => ({
    therapySequence: one(patientTherapySequence, {
        fields: [patientTherapySequenceObservations.therapySequenceId],
        references: [patientTherapySequence.id],
    }),
    created: one(user, {
        fields: [patientTherapySequenceObservations.createdBy],
        references: [user.id],
    }),
}));
export const patientTherapySequenceConsumableRelations = relations(patientTherapySequenceConsumables, ({ one }) => ({
    therapySequence: one(patientTherapySequence, {
        fields: [patientTherapySequenceConsumables.therapySequenceId],
        references: [patientTherapySequence.id],
    }),
    consumable: one(consumables, {
        fields: [patientTherapySequenceConsumables.consumableId],
        references: [consumables.id],
    }),
    created: one(user, {
        fields: [patientTherapySequenceConsumables.createdBy],
        references: [user.id],
    }),
}));
//# sourceMappingURL=patient-therapy.js.map